import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Search } from "lucide-react";
import { api, CITIES, CATEGORIES } from "@/lib/api";
import { HostessCard } from "@/components/HostessCard";
import { Seo } from "@/components/Seo";

const SORTS = [["online", "Online di recente", "Online"], ["popular", "Più popolari", "Popolari"], ["newest", "Nuove iscritte", "Nuove"]];

const Pill = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${active ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 text-neutral-400 hover:border-white/30 hover:text-white"}`}>
    {children}
  </button>
);

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const city = params.get("city") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "online";
  const [cityInput, setCityInput] = useState(city);
  const [list, setList] = useState(null);

  useEffect(() => {
    setList(null);
    api.get("/hostesses", { params: { city, category, sort } }).then((r) => setList(r.data));
  }, [city, category, sort]);

  const update = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    setParams(p);
  };

  useEffect(() => {
    const v = cityInput.trim();
    if (v === city) return;
    const t = setTimeout(() => update("city", v), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityInput]);

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-16" data-testid="search-page">
      <Seo title={`Cerca hostess, modelle e wingwoman${city ? ` a ${city}` : ""} | YourHostess`} description={`Sfoglia i profili di hostess, modelle e wingwoman${city ? ` a ${city}` : " in tutta Italia"}: filtra per città e categoria e trova la persona giusta per il tuo evento.`} />
      <p className="eyebrow">Directory</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3">Trova la tua hostess</h1>

      <div className="mt-12 grid lg:grid-cols-12 gap-8 items-end">
        <form onSubmit={(e) => { e.preventDefault(); update("city", cityInput.trim()); }} className="lg:col-span-5 flex items-center gap-3 border-b border-white/15 focus-within:border-[#D4AF37] transition-colors" data-testid="search-city-form">
          <MapPin className="w-4 h-4 text-[#D4AF37]" />
          <input value={cityInput} onChange={(e) => setCityInput(e.target.value)} placeholder="Cerca per città" className="bg-transparent flex-1 py-3 focus:outline-none placeholder:text-neutral-600" data-testid="search-city-input" />
          <button className="text-neutral-400 hover:text-[#D4AF37]" data-testid="search-city-submit" aria-label="Cerca"><Search className="w-4 h-4" /></button>
        </form>
        <div className="lg:col-span-7 flex flex-wrap gap-2">
          {CITIES.map((c) => <Pill key={c} active={city.toLowerCase() === c.toLowerCase()} onClick={() => { setCityInput(city === c ? "" : c); update("city", city === c ? "" : c); }} testid={`search-city-pill-${c}`}>{c}</Pill>)}
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        <Pill active={!category} onClick={() => update("category", "")} testid="search-category-all">Tutte</Pill>
        {CATEGORIES.map((c) => <Pill key={c} active={category === c} onClick={() => update("category", c)} testid={`search-category-${c}`}>{c}</Pill>)}
      </div>

      <div className="mt-12 mb-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs font-mono text-neutral-500" data-testid="search-results-count">
          {list ? (city && !list.length ? `Nessun profilo entro 100 km da ${city}` : `${list.length} ${list.length === 1 ? "profilo" : "profili"}${city ? ` entro 100 km da ${city}` : ""}`) : "Caricamento…"}
        </p>
        <div className="flex items-center gap-1 border border-white/10 p-1" data-testid="search-sort">
          <span className="eyebrow !text-neutral-500 px-2 hidden sm:inline">Ordina</span>
          {SORTS.map(([v, l, short]) => (
            <button key={v} onClick={() => update("sort", v === "online" ? "" : v)} data-testid={`search-sort-${v}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.12em] transition-colors whitespace-nowrap ${sort === v ? "bg-[#D4AF37] text-[#0B0C0E]" : "text-neutral-400 hover:text-white"}`}>
              <span className="hidden sm:inline">{l}</span><span className="sm:hidden">{short}</span>
            </button>
          ))}
        </div>
      </div>
      {list && list.length === 0 ? (
        <div className="border border-white/5 py-24 text-center" data-testid="search-empty">
          <p className="font-serif text-3xl">Nessun profilo trovato</p>
          <p className="text-sm text-neutral-500 mt-3">Prova un'altra città o pubblica un evento: le hostess disponibili si candideranno.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="search-results-grid">
          {(list || []).map((h, i) => <HostessCard key={h.id} h={h} index={i} />)}
        </div>
      )}
    </main>
  );
}
